import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { once, scheduleOnce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormItemGroupCloneable from 'webapp/models-custom/custom-metric-setup-form/item/group/cloneable';
import type CustomMetricSetupFormItemGroupCommon from 'webapp/models-custom/custom-metric-setup-form/item/group/common';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';

export type CustomMetricSetupForm = CustomMetricSetupFormPrebuilt | CustomMetricSetupFormSheet;
export type CustomMetricSetupFormItemGroup =
	CustomMetricSetupFormItemGroupCommon | CustomMetricSetupFormItemGroupCloneable;

interface Args {
	form: CustomMetricSetupForm;
	model: CustomMetricSetupFormItemGroup;
	isDisabled: boolean;
	onContentChange: () => void;
}

export default class CustomQuerySetupPrebuiltFormGroupComponent extends Component<Args> {
	@tracked isOpen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		let isOpen = true;
		if (this.args.model.isToggler && this.args.model.isDefault) {
			isOpen = false;
		}
		this.isOpen = isOpen;

		if (this.args.model.depends) {
			scheduleOnce('afterRender', this, this.checkIfNeedToHide);
		}
	}

	get modelAsCloneable() {
		return this.args.model as CustomMetricSetupFormItemGroupCloneable;
	}

	get modelAsNotCloneable() {
		return this.args.model as CustomMetricSetupFormItemGroupCommon;
	}

	get shownItemsForCloneable() {
		return this.modelAsCloneable.shownItems;
	}

	get canAddCloneable(): boolean {
		return (
			this.modelAsCloneable.isCloneable &&
			this.modelAsCloneable.max > this.modelAsCloneable.shownItems.length
		);
	}

	checkIfNeedToHide(): void {
		let doHide = false;

		// group does not support dependsShow and dependsHide
		const depends = this.args.model.depends;
		let dependsField;
		if (typeof depends === 'string') {
			dependsField = this.getOtherField(depends);
		}
		if (dependsField?.isHidden) {
			doHide = true;
		} else {
			const value = this.getOtherFieldValue(this.args.model.depends);
			doHide = isEmpty(value);
		}

		if (this.args.model.isHidden !== doHide) {
			this.args.model.isHidden = doHide;
		}
	}

	private getOtherField(fieldId: string): CustomMetricSetupFormItemField {
		// does not support nested cloneable groups inside cloneable groups
		return this.args.form.items.find(
			(item) => item.id === fieldId,
		) as CustomMetricSetupFormItemField;
	}

	private getOtherFieldValue(fieldId: string | string[]) {
		let value;
		// parameters can be also arrays
		if (typeof fieldId === 'string') {
			// groupitems first, if exist
			const item = this.getOtherField(fieldId);
			value = item ? item.value : null;
		} else {
			value = [];
			fieldId.forEach((fId) => {
				const v = this.getOtherFieldValue(fId);
				if (v) {
					value.push(v);
				}
			});
		}
		return value;
	}

	@action
	onSomeValueChange(): void {
		if (this.args.model.depends) {
			once(this, this.checkIfNeedToHide);
		}
	}

	@action
	toggle() {
		this.isOpen = !this.isOpen;
		this.args.onContentChange();
	}

	@action
	addCloneable() {
		if (!this.args.isDisabled) {
			const group = this.args.model as CustomMetricSetupFormItemGroupCloneable;
			group.addShownItem();
			this.args.onContentChange();
		}
	}
	<template>
		<div
			class="group row {{if this.isOpen 'open'}} {{if @model.isToggler 'togglebox'}}"
			{{didInsert @onContentChange}}
			{{didUpdate @onContentChange @model.isHidden @model.shownItems}}
			{{didUpdate this.onSomeValueChange @form.valueChangeCounter}}
			{{willDestroy @onContentChange}}
		>
			{{#if @model.isToggler}}
				<a role="button" class="toggler" {{on "click" this.toggle}}>
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />{{@model.name}}
					{{#if @model.description}}
						<ToolTipWithIconComponent @text={{@model.description}} @theme="dark" />
					{{/if}}
				</a>
			{{/if}}

			{{#if @model.isCloneable}}
				{{#if @model.name}}
					<div class="item-title">
						<label class="title">
							{{@model.name}}
						</label>
						{{#if @model.description}}
							<ToolTipWithIconComponent @text={{@model.description}} @theme="dark" />
						{{/if}}
					</div>
				{{/if}}
			{{/if}}

			<div class="items">
				{{#if @model.isCloneable}}
					{{#each this.modelAsCloneable.shownItems as |shownItem index|}}
						{{#let (component @model.shownItemsComponent) as |Component|}}
							<Component
								@form={{@form}}
								@group={{this.modelAsCloneable}}
								@model={{shownItem}}
								@index={{index}}
								@canAdd={{this.canAddCloneable}}
								@onContentChange={{@onContentChange}}
								@isDisabled={{@isDisabled}}
							/>
						{{/let}}
					{{/each}}
				{{else}}
					{{#each this.modelAsNotCloneable.shownItems as |shownItem|}}
						{{#let (component @model.shownItemsComponent) as |Component|}}
							<Component
								@form={{@form}}
								@model={{shownItem}}
								@onContentChange={{@onContentChange}}
								@isDisabled={{@isDisabled}}
							/>
						{{/let}}
					{{/each}}
				{{/if}}

				{{#if this.canAddCloneable}}
					<a
						class="add {{if @isDisabled 'disabled'}}"
						{{on "click" this.addCloneable}}
					><CommonUiIconComponent @name="Plus" @size="lg" />
					</a>
				{{/if}}
			</div>
		</div>
	</template>
}
